import { VisibilityBadge } from '@/components/shared';
import type { ChallengeTypeIcons } from '@/config/objects/events';
import type {
  ChallengeStack,
  PlacedArtifact,
  PlacedCollectible,
} from '@/config/objects/placements';
import { renderDateCell } from '@/lib/listCells';
import type { CrudConfig } from '@startupquickstart/frontend';

export type Visibility = 'private' | 'unlisted' | 'public';

export interface Template {
  id: string;
  organizationId: string;
  name: string;
  description?: string | null;
  estimatedDuration?: string | null;
  tags: string[];
  coverImageUrl?: string | null;
  introVideoUrl?: string | null;
  visibility: Visibility;
  isFeatured: boolean;
  defaultSettings?: Record<string, unknown> | null;
  // Branding — mirrors the event branding fields (DT-162 family) so a
  // template carries a full branded experience into the events it stamps
  // out. Each field is null/empty when the template hasn't been branded.
  logoUrl?: string | null;
  logoSmallUrl?: string | null;
  backgroundColor?: string | null;
  accentColor?: string | null;
  theme?: string;
  customPointsLabel?: string | null;
  hidePoiOrder?: boolean;
  permitActivateOnPin?: boolean;
  slideshowIntervalSeconds?: number;
  /**
   * Per-template default map-icon overrides keyed by challenge type —
   * same record shape as `Event.challengeTypeIcons` (DT-165 phase B).
   */
  challengeTypeIcons?: ChallengeTypeIcons | null;
  createdAt: string;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
  } | null;
  // Related data from challenges endpoint
  _count?: {
    challenges: number;
  };
}

// Template challenge link (for linked challenges)
export interface TemplateChallengeLink {
  id: string;
  templateId: string;
  libraryChallengeId: string;
  groupId?: string | null;
  sortOrder: number;
  isStarter: boolean;
  requireAll: boolean;
  maxCompletions?: number | null;
  speedBonuses: number[];
  // Per-template pin override (null = inherit the library challenge location).
  latitude?: number | null;
  longitude?: number | null;
  /** Stack membership (DT-170 parity) — null when not part of a stack. */
  stackId?: string | null;
  createdAt: string;
  updatedAt: string;
  libraryChallenge: {
    id: string;
    name: string;
    description?: string | null;
    clue?: string;
    hint?: string | null;
    type: string;
    difficulty: string;
    points: number;
    zone?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    requiresPhoto?: boolean;
    tags?: string[];
    imageUrl?: string | null;
  };
}

// Group unlock modes
export type GroupUnlockMode = 'all_at_once' | 'sequential';

// Group unlock condition types
export type GroupUnlockCondition = 'starter' | 'after_challenge' | 'after_group';

// Template group unlock condition
export interface TemplateGroupUnlock {
  id: string;
  groupId: string;
  conditionType: GroupUnlockCondition;
  triggerChallengeLinkId?: string | null;
  triggerGroupId?: string | null;
  triggerChallenge?: {
    id: string;
    libraryChallenge: {
      id: string;
      name: string;
    };
  } | null;
  triggerGroup?: {
    id: string;
    name: string;
  } | null;
}

// Template challenge group
export interface TemplateChallengeGroup {
  id: string;
  templateId: string;
  name: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  sortOrder: number;
  unlockMode: GroupUnlockMode;
  createdAt: string;
  updatedAt: string;
  challenges: TemplateChallengeLink[];
  unlockConditions: TemplateGroupUnlock[];
}

/**
 * Template challenge stack — same wire shape as an event's stack
 * (`/templates/:id/stacks` mirrors `/events/:id/stacks`).
 */
export type TemplateStack = ChallengeStack;

/** Artifact placed in a template from the library (mirrors EventArtifact). */
export interface TemplateArtifact extends PlacedArtifact {
  templateId: string;
}

/** Collectible added to a template from the library (mirrors the event shape). */
export interface TemplateCollectible extends PlacedCollectible {
  templateId: string;
}

/**
 * Default team seeded into every event created from the template
 * (`/templates/:id/teams`).
 */
export interface TemplateDefaultTeam {
  id: string;
  templateId: string;
  name: string;
  emoji?: string | null;
  iconName: string;
  iconColor: string;
  photoUrl?: string | null;
  isStaff: boolean;
  sortOrder: number;
}

export const templateCrudConfig: CrudConfig<Template> = {
  entityName: 'Template',
  entityNamePlural: 'Templates',

  endpoints: {
    list: '/templates',
    single: (id: string) => `/templates/${id}`,
    create: '/templates',
    update: (id: string) => `/templates/${id}`,
    delete: (id: string) => `/templates/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter template name',
      validation: {
        min: 1,
        max: 200,
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter a description',
    },
    {
      key: 'estimatedDuration',
      label: 'Estimated Duration',
      type: 'text',
      required: false,
      placeholder: 'e.g., 2 hours',
      helpText: 'Approximate time to complete the hunt',
    },
    {
      key: 'coverImageUrl',
      label: 'Cover Image URL',
      type: 'text',
      required: false,
      placeholder: 'Enter cover image URL',
    },
    {
      key: 'visibility',
      label: 'Visibility',
      type: 'select',
      required: true,
      defaultValue: 'private',
      options: [
        { label: 'Private', value: 'private' },
        { label: 'Unlisted', value: 'unlisted' },
        { label: 'Public', value: 'public' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return <VisibilityBadge visibility={value as Visibility} />;
      },
    },
    {
      key: 'isFeatured',
      label: 'Featured',
      type: 'select',
      required: false,
      defaultValue: false,
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      format: (value: boolean) => {
        if (value) {
          return (
            <span className="inline-flex items-center rounded-full bg-amber-400/15 px-2.5 py-0.5 text-xs font-medium text-amber-600">
              Featured
            </span>
          );
        }
        return null;
      },
    },
    {
      key: 'tags',
      label: 'Tags',
      type: 'text',
      required: false,
      placeholder: 'Enter tags (comma separated)',
    },
    {
      key: 'createdAt',
      label: 'Created',
      type: 'datetime',
      readonly: true,
      format: renderDateCell,
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      type: 'datetime',
      readonly: true,
      format: renderDateCell,
    },
  ],

  listView: {
    fields: ['name', 'visibility', 'isFeatured', 'estimatedDuration', 'updatedAt'],
    // Phones keep the two leading columns; the rest is desktop
    // detail (hideOnMobile) so the table never scrolls sideways.
    columns: [
      { field: 'name' },
      { field: 'visibility' },
      { field: 'isFeatured', hideOnMobile: true },
      { field: 'estimatedDuration', hideOnMobile: true },
      { field: 'updatedAt', hideOnMobile: true },
    ],
    searchableFields: ['name', 'description'],
    sortableFields: ['name', 'visibility', 'isFeatured', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'updatedAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    // Saved lists + the filter panel are intentionally disabled for now
    // (search + column sorting only). Filter definitions live in git history
    // (PR #369-era configs) — restore them here to re-enable both.
    filters: [],
    actions: {
      create: { enabled: true },
      view: { enabled: true },
      edit: { enabled: true },
      delete: { enabled: true },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Template Details',
        description: 'Basic template information',
        fields: ['visibility', 'isFeatured', 'estimatedDuration'],
        defaultExpanded: true,
      },
      {
        title: 'Description',
        description: 'Template description',
        fields: ['description'],
        defaultExpanded: true,
      },
      {
        title: 'Media',
        description: 'Cover image',
        fields: ['coverImageUrl'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and metadata',
        fields: ['tags', 'createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'Template Details',
        description: 'Basic information about the template',
        fields: ['name', 'description', 'estimatedDuration', 'coverImageUrl'],
        columns: 1,
      },
      {
        title: 'Visibility',
        description: 'Control who can see this template',
        fields: ['visibility', 'isFeatured'],
        columns: 2,
      },
      {
        title: 'Tags',
        description: 'Categorize this template',
        fields: ['tags'],
        columns: 1,
      },
    ],
    validation: {
      name: { required: true, min: 1, max: 200 },
    },
  },
};
